'use client';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { usePathname, useRouter } from 'next/navigation';
import React, { useCallback } from 'react';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { isPC } from 'constants/layout';
import { ArrowIcon } from 'icons/ArrowIcon';
import { CloseIcon } from 'icons/CloseIcon';
import { FavoriteIcon } from 'icons/FavoriteIcon';
import { HomeIcon } from 'icons/HomeIcon';
import { InfoIcon } from 'icons/InfoIcon';
import { ListIcon } from 'icons/ListIcon';
import { NotiIcon } from 'icons/NotiIcon';
import { ReplyIcon } from 'icons/ReplyIcon';
import { SettingIcon } from 'icons/SettingIcon';
import { ShareIcon } from 'icons/ShareIcon';
import { DirittoAppBridge } from 'libs/DirittoAppBridge';
import { IconButton } from 'ui-components/common/IconButton';
import { Link } from 'ui-components/common/Link';
import { useClient } from 'ui-hooks/useClient';
import { useRouterBack } from 'ui-hooks/useRouterBack';
import { useShare } from 'ui-hooks/useShare';
import { configure } from 'utils/configure';

interface HeaderButtonProps {
  color?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  mobileOnly?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  // eslint-disable-next-line react/no-unused-prop-types
  onClickCapture?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  // eslint-disable-next-line react/no-unused-prop-types
  onPreClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  // eslint-disable-next-line react/no-unused-prop-types
  disabled?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  className?: string;
}

interface LinkHeaderButtonProps extends HeaderButtonProps {
  href: string;
}

interface ToggleButtonProps extends HeaderButtonProps {
  fill: boolean;
  opacity?: number;
}

export const TextButton = ({
  label,
  color = Color.Black,
  onClick,
}: {
  label: string;
  onClick?: () => void;
  color?: string;
}) => (
  <StyledTextButton type="button" onClick={onClick} color={color}>
    {label}
  </StyledTextButton>
);

export const TextCloseButton = ({ onClick }: { onClick?: () => void }) => {
  const routerBack = useRouterBack();

  const handleBack = useCallback(() => {
    if (onClick) {
      onClick?.();
    } else {
      routerBack();
    }
  }, [onClick, routerBack]);

  return (
    <StyledTextButton type="button" onClick={handleBack}>
      닫기
    </StyledTextButton>
  );
};

const StyledTextButton = styled.button(
  ({ color = Color.Black }: { color?: string }) => css`
    flex-grow: 0;
    flex-shrink: 0;
    color: ${color};
    ${Typography.XSmall};
    font-weight: ${FontWeight.Medium};
  `,
);

export const CloseButton = ({ onClick, color, mobileOnly }: HeaderButtonProps) => (
  <StyledIconButton onClick={onClick} data-mobile-only={mobileOnly}>
    <CloseIcon color={color} />
  </StyledIconButton>
);

export const BackButton = ({ color = Color.Black, onClick, onPreClick, className }: HeaderButtonProps) => {
  const routerBack = useRouterBack();
  const client = useClient();

  const handleBack = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onPreClick?.(e);

      if (onClick) {
        onClick?.(e);
      } else if (client.isApp) {
        DirittoAppBridge.historyBack();
      } else {
        routerBack();
      }
    },
    [client.isApp, onClick, onPreClick, routerBack],
  );

  return (
    <StyledBackIconButton onClick={handleBack} className={className}>
      <ArrowIcon color={color} direction="left" />
    </StyledBackIconButton>
  );
};

export const CloseLinkButton = ({ href, color, mobileOnly }: LinkHeaderButtonProps) => (
  <StyledIconButton data-mobile-only={mobileOnly}>
    <Link href={href}>
      <CloseIcon color={color} />
    </Link>
  </StyledIconButton>
);

export const HomeButton = ({ color, mobileOnly, onClick, onPreClick }: HeaderButtonProps) => {
  const client = useClient();
  const router = useRouter();

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onPreClick?.(e);

      if (onClick) {
        onClick?.(e);
      } else if (client.isApp) {
        DirittoAppBridge.resetToHome();
      } else {
        router.push('/');
      }
    },
    [client.isApp, onClick, onPreClick, router],
  );

  return (
    <StyledIconButton type="button" data-mobile-only={mobileOnly} onClick={handleClick}>
      <HomeIcon color={color} fill={false} />
    </StyledIconButton>
  );
};

export const ListLinkButton = ({ href, color, mobileOnly }: LinkHeaderButtonProps) => (
  <StyledIconButton data-mobile-only={mobileOnly}>
    <Link href={href}>
      <ListIcon color={color} />
    </Link>
  </StyledIconButton>
);

export const ListButton = ({ onClick, onClickCapture, color, mobileOnly }: HeaderButtonProps) => (
  <StyledIconButton onClick={onClick} onClickCapture={onClickCapture} data-mobile-only={mobileOnly}>
    <ListIcon color={color} />
  </StyledIconButton>
);

export const InfoButton = ({ onClick, onClickCapture, color, mobileOnly, disabled }: HeaderButtonProps) => (
  <StyledIconButton
    onClick={disabled ? undefined : onClick}
    onClickCapture={disabled ? undefined : onClickCapture}
    data-mobile-only={mobileOnly}>
    <InfoIcon color={color} opacity={disabled ? 0.3 : 1} />
  </StyledIconButton>
);

export const NotiButton = ({ onClick, color = Color.Black, fill, opacity, mobileOnly }: ToggleButtonProps) => (
  <StyledIconButton onClick={onClick} data-mobile-only={mobileOnly}>
    <NotiIcon fill={fill} fillColor={Color.Primary500} color={color} opacity={opacity} />
  </StyledIconButton>
);

export const FavoriteButton = ({ onClick, color = Color.Black, fill, opacity, mobileOnly }: ToggleButtonProps) => (
  <StyledIconButton onClick={onClick} data-mobile-only={mobileOnly}>
    <FavoriteIcon fill={fill} fillColor={Color.Primary500} color={color} opacity={opacity} />
  </StyledIconButton>
);

export const CommentLinkButton = ({ href, color = Color.Black, mobileOnly }: LinkHeaderButtonProps) => (
  <StyledIconButton data-mobile-only={mobileOnly}>
    <Link href={href}>
      <ReplyIcon color={color} />
    </Link>
  </StyledIconButton>
);

export const SettingButton = ({
  onClick,
  onClickCapture,
  color,
  mobileOnly,
  isNew = false,
}: HeaderButtonProps & { isNew?: boolean }) => (
  <StyledIconButton onClick={onClick} onClickCapture={onClickCapture} data-mobile-only={mobileOnly}>
    {isNew && <RedDot />}
    <SettingIcon color={color} />
  </StyledIconButton>
);

export const ShareButton = ({ params, onClick }: { params?: ShareData; onClick?: () => void }) => {
  const handleShare = useShare();
  const pathname = usePathname();
  const thisUrl = `${configure.serviceUrl}${pathname}`;

  return (
    <StyledIconButton onClick={() => onClick?.() ?? handleShare(params ?? { url: thisUrl })}>
      <ShareIcon />
    </StyledIconButton>
  );
};

const RedDot = styled.span`
  position: absolute;
  top: 0px;
  right: 0px;
  width: 4px;
  height: 4px;
  background: ${Color.Red500};
  border-radius: 50%;
  z-index: 1;
  pointer-events: none;
`;

const StyledIconButton = styled(IconButton)(
  ({ mobileOnly = false }: { mobileOnly?: boolean }) => css`
    width: 24px;
    height: 24px;
    border-radius: 3px;
    display: block;
    position: relative;

    @media (${isPC}) {
      display: ${mobileOnly ? 'none' : 'block'};
    }
  `,
);

const StyledBackIconButton = styled(StyledIconButton)`
  width: 24px;
  height: 24px;
`;
